import { useRef, useCallback, useEffect } from 'react';

const MAX_REFRESH_LIMIT = 10;

export const useRefreshSession = ({ user, userSession, onRefresh }) => {
  const timerId = useRef(null);
  const refreshCount = useRef(0);

  const clearTimer = useCallback(() => {
    clearTimeout(timerId.current);
    timerId.current = null;
  }, []);

  const refreshSession = useCallback(() => {
    refreshCount.current += 1;

    user?.refreshSession(userSession.refreshToken, (err, session) => {
      if (err) return;
      onRefresh(session);
    });
  }, [user, userSession, onRefresh, clearTimer]);

  const scheduleRefresh = useCallback(
    timing => {
      const scheduleTime = Math.max(timing - 2000, 0);

      timerId.current = setTimeout(refreshSession, scheduleTime);
    },
    [refreshSession, clearTimer],
  );

  useEffect(() => {
    clearTimer();
    if (!userSession || !user) return;
    const { exp } = userSession.accessToken.payload;

    const expirationTimeInMilliseconds = exp * 1000;
    const expiration = new Date(expirationTimeInMilliseconds);
    const now = new Date();
    const timeDifference = expiration - now;
    const isExpired = timeDifference <= 0;

    if (refreshCount.current >= MAX_REFRESH_LIMIT) return;

    if (isExpired) {
      refreshSession();
      return;
    }

    scheduleRefresh(timeDifference);
  }, [userSession, user, refreshSession, scheduleRefresh]);
};
